/**
 * This React provider checks that the user is logged in. It allows child
 * components to call useAdmin() and useTenant() to get information about the
 * logged-in admin and their tenant.
 */
import { ApolloError, ServerError, useQuery } from "@apollo/client";
import { Button } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import React, { ReactElement, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { StringParam, useQueryParam } from "use-query-params";

import {
  FeatureFlags,
  parseFeatureFlags,
  parsePartialFeatureFlags,
} from "@stw/feature-flags";

import {
  LoggedInInfoQuery,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import FullPageCard from "~frontend/components/FullPageCard";
import { FullPageLoading } from "~frontend/components/Loading";
import LogOutLink from "~frontend/components/LogOutLink";
import { LOGGED_IN_INFO } from "~frontend/graphql/loggedInInfo";
import useNewVersionAlert from "~frontend/hooks/useNewVersionAlert";
import { useClock } from "~frontend/util/useClock";

import { useIntl } from "../IntlProvider";
import { useNonCacheClearingApolloClient } from "../NonCacheClearingApolloClientProvider";
import { DeactivatedTenant } from "./DeactivatedTenant";
import LoginPage from "./LoginPage";
import { LoggedInUserContext } from "./context";

export {
  useCurrentUserIsStaff,
  useCurrentUser,
  useTenant,
  useLoginInfoRefetch,
} from "./context";

export type {
  LoggedInUser,
  Tenant,
  UserSubaccountMembership,
  SubaccountBaseData as Subaccount,
  SubaccountRole,
} from "./context";

const SESSION_EXPIRATION_WARNING_MINUTES = 15;

function ErrorPage({ error }: { error: ApolloError }): ReactElement {
  const serverError = error.networkError as ServerError | undefined;

  let title: React.ReactNode;
  let message: React.ReactNode;
  if (serverError?.statusCode && serverError?.statusCode >= 500) {
    title = (
      <FormattedMessage
        defaultMessage="Server error"
        description="Title for server error message"
      />
    );
    message = (
      <FormattedMessage
        defaultMessage="We encountered an error processing your request. Please try again later."
        description="Message for server error"
      />
    );
  } else if (serverError?.statusCode && serverError?.statusCode === 410) {
    title = (
      <FormattedMessage
        defaultMessage="Organization Deactivated"
        description="Title for deactivated organization error message"
      />
    );

    message = (
      <FormattedMessage
        defaultMessage="This organization has been deactivated. Please contact your organization administrator or {emailLink} if you have questions or if you'd like to reactivate your organization."
        description="Message for deactivated organization error"
        values={{
          emailLink: (
            <a href="mailto:support@scaletowin.com">support@scaletowin.com</a>
          ),
        }}
      />
    );
  } else if (serverError?.statusCode && serverError?.statusCode >= 400) {
    title = (
      <FormattedMessage
        defaultMessage="Request error"
        description="Error message for an invalid request during email verification"
      />
    );
    message = (
      <FormattedMessage
        defaultMessage="We were unable to load your user account data due to an invalid request. Please try again later."
        description="Error message for an invalid request during email verification"
      />
    );
  } else if (serverError?.message === "Failed to fetch") {
    title = (
      <FormattedMessage
        defaultMessage="Network error"
        description="Title for network error message"
      />
    );
    message = (
      <FormattedMessage
        defaultMessage="We were unable to load your user account data due to a network error. Please check your internet connection and try again."
        description="Message for network error"
      />
    );
  } else {
    title = (
      <FormattedMessage
        defaultMessage="Unknown error"
        description="Title for unknown error message"
      />
    );
    message = (
      <FormattedMessage
        defaultMessage="Error loading data. Please check your internet connection and try again."
        description="Message for unknown error"
      />
    );
  }

  return (
    <FullPageCard title={title}>
      <p>{message}</p>
      <LogOutLink />
    </FullPageCard>
  );
}

function SessionExpirationWarning({ expiresAt }: { expiresAt: DateTime }) {
  const now = useClock();

  return (
    <FormattedMessage
      defaultMessage="Your session will expire in {expiresInMinutes} {expiresInMinutes, plural,
          =1 {minute}
          other {minutes}
        }. Please save your work and log back in."
      description="Warning message for session expiration"
      values={{
        expiresInMinutes: Math.floor(
          Math.max(expiresAt.diff(now, "minutes").minutes, 0)
        ),
      }}
    />
  );
}

export default function LoggedInUserProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement | null {
  useNewVersionAlert();
  const client = useNonCacheClearingApolloClient();
  const { setLanguage } = useIntl();

  const [inviteEmail, setInviteEmail] = useQueryParam(
    "inviteEmail",
    StringParam
  );

  const { loading, error, data, refetch } = useQuery<LoggedInInfoQuery>(
    LOGGED_IN_INFO,
    {
      pollInterval: 30 * 1000,
      fetchPolicy: "network-only",
      client,
    }
  );

  // we save `data` so that if a poll iteration fails, we can keep using the
  // old data. We also use this useEffect to update the intl provider with the
  // new locale.
  const [dataCache, setDataCache] = React.useState<{
    loggedInInfo: LoggedInInfoQuery["loggedInInfo"];
    featureFlags: FeatureFlags;
    featureFlagDefaults: FeatureFlags;
  } | null>(null);

  const [sessionExpirationWarningShown, setSessionExpirationWarningShown] =
    React.useState(false);

  const now = useClock(1000 * 60);

  useEffect(() => {
    const serverError = error?.networkError as ServerError | undefined;
    if (serverError?.statusCode === 401) {
      // if we get a 401, the user is no longer logged in
      setDataCache(null);
      return;
    } else if (data && !error) {
      const featureFlagDefaults = parsePartialFeatureFlags(
        JSON.parse(data.loggedInInfo.featureFlagDefaults)
      );
      setDataCache({
        loggedInInfo: data.loggedInInfo,
        featureFlags: parseFeatureFlags(
          data.loggedInInfo.tenant.featureFlags,
          featureFlagDefaults
        ),
        featureFlagDefaults: parseFeatureFlags({}, featureFlagDefaults),
      });
      setLanguage(data.loggedInInfo.currentUser.language || null);
    }
  }, [data, error, setLanguage]);

  useEffect(() => {
    if (dataCache && inviteEmail) {
      if (inviteEmail === dataCache.loggedInInfo.currentUser.email) {
        setInviteEmail(undefined);
      } else {
        // The user clicked on an invite link, but they're already logged in as a different user.
        // We need to log them out and back in as the correct user.
        window.location.href = "/auth/logout";
      }
    }
  }, [dataCache, inviteEmail, setInviteEmail]);

  const snackbar = useSnackbar();
  useEffect(() => {
    if (!dataCache) {
      return;
    }

    if (sessionExpirationWarningShown) {
      return;
    }

    const sessionExpiresAt = DateTime.fromMillis(
      dataCache.loggedInInfo.currentSessionExpiresAt
    );
    const timeUntilSessionExpiration = sessionExpiresAt.diff(
      now,
      "minutes"
    ).minutes;

    if (timeUntilSessionExpiration < SESSION_EXPIRATION_WARNING_MINUTES) {
      const loginParams = new URLSearchParams({
        return_to: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        screen_hint: "login",
        login_hint: dataCache.loggedInInfo.currentUser.email,
      });

      const currentUserIsStaff =
        dataCache.loggedInInfo.currentUser.tenantRole === UserTenantRole.Staff;
      const loginTarget = `/auth/${currentUserIsStaff ? "staff-login" : "login"}?${loginParams.toString()}`;

      setSessionExpirationWarningShown(true);
      snackbar.enqueueSnackbar(
        <SessionExpirationWarning expiresAt={sessionExpiresAt} />,
        {
          variant: "warning",
          persist: true,
          action: () => (
            <Button
              onClick={() => {
                document.location = loginTarget;
              }}
            >
              Log Back In
            </Button>
          ),
        }
      );
    }
  }, [dataCache, sessionExpirationWarningShown, now, snackbar]);

  if (dataCache) {
    if (
      dataCache.loggedInInfo.tenant.deactivatedAt &&
      dataCache.loggedInInfo.currentUser.tenantRole !== UserTenantRole.Staff
    ) {
      return (
        <DeactivatedTenant
          tenant={dataCache.loggedInInfo.tenant}
          user={dataCache.loggedInInfo.currentUser}
        />
      );
    }

    return (
      <LoggedInUserContext.Provider
        value={{
          loggedInInfo: dataCache.loggedInInfo,
          featureFlags: parseFeatureFlags(
            dataCache.featureFlags,
            dataCache.featureFlagDefaults
          ),
          defaultFeatureFlags: parseFeatureFlags(
            {},
            dataCache.featureFlagDefaults
          ),
          refetch,
        }}
      >
        {children}
      </LoggedInUserContext.Provider>
    );
  }

  if (error) {
    const serverError = error.networkError as ServerError | undefined;
    if (serverError?.statusCode === 401) {
      return <LoginPage />;
    }

    return (
      <LoggedInUserContext.Provider
        value={{
          loggedInInfo: null,
          featureFlags: parseFeatureFlags({}, {}),
          defaultFeatureFlags: parseFeatureFlags({}, {}),
          refetch,
        }}
      >
        <ErrorPage error={error} />
      </LoggedInUserContext.Provider>
    );
  }

  if (loading) {
    return <FullPageLoading />;
  }

  return null;
}
